import React, { useRef, useState, useMemo, useCallback, KeyboardEvent, memo, useEffect } from 'react';
import { NavigatorItem, GAPromotionConfig } from '@box-types';
import { useInjector } from '@box-react/hooks/use-injector';
import { AnalyticsService } from '@box-core/services';
import { generateNewCDNImageSrc, HomeSection } from '@box/utils';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Mousewheel } from 'swiper/modules';
import { Swiper as SwiperType } from 'swiper/types';
import { CollapsibleTileComponent } from '@box-react/components';
import isEqual from 'lodash-es/isEqual';

type NavigatorSectionProps = {
  homeSection: HomeSection;
  onRouterClick: (s: string) => void;
};

function NavigatorSectionComponent({ homeSection, onRouterClick }: NavigatorSectionProps) {
  const analyticsService = useInjector().get(AnalyticsService);

  const [swiperInstance, setSwiperInstance] = useState<SwiperType | null>(null);
  const [showArrows, setShowArrows] = useState(false);

  const prevEl = useRef<HTMLButtonElement>(null);
  const nextEl = useRef<HTMLButtonElement>(null);

  const mainViewTiles = useMemo(() => homeSection?.navigatorItems ?? [], [homeSection?.navigatorItems]);

  const getCollapsibleTiles = (tiles: typeof mainViewTiles) => {
    return tiles.map((tile) => ({
      title: tile.title,
      image: generateNewCDNImageSrc(tile.image, 'NAVIGATOR_ITEM') ?? '',
      // image: '',
      action: tile.action
    }));
  };
  const collapsibleTiles = useMemo(() => getCollapsibleTiles(mainViewTiles), [mainViewTiles]);

  const title = homeSection?.title || 'Cuisines';

  // Tracks which slides have already fired a view event
  const trackedSlides = useRef<Record<string, boolean>>({});

  const triggerAnalyticsEvent = useCallback(
    (eventName: string, navigatorItem: NavigatorItem, position: number) => {
      const gaConfig = {
        parameter_name_1: position,
        parameter_name_2: navigatorItem.slug
      } as GAPromotionConfig;
      analyticsService.addGACustomEvent(eventName, gaConfig);
    },
    [analyticsService]
  );

  const onItemEnteredViewport = useCallback(
    (navigatorItem: NavigatorItem, position: number) => {
      if (!trackedSlides.current[navigatorItem.slug]) {
        triggerAnalyticsEvent('generic_view_navigator', navigatorItem, position);
        trackedSlides.current[navigatorItem.slug] = true;
      }
    },
    [triggerAnalyticsEvent]
  );

  const checkVisibleSlides = useCallback(() => {
    if (!swiperInstance) return;

    swiperInstance.slides.forEach((slide, index) => {
      const boundingRect = slide.getBoundingClientRect();
      const viewportWidth = window.innerWidth || document.documentElement.clientWidth;

      // Check if slide is fully visible in viewport
      const isFullyVisible = boundingRect.left >= 0 && boundingRect.right <= viewportWidth;

      if (isFullyVisible) {
        const navigatorItem = mainViewTiles[index];
        if (navigatorItem) {
          onItemEnteredViewport(navigatorItem, index + 1);
        }
      }
    });
  }, [swiperInstance, mainViewTiles, onItemEnteredViewport]);

  const onSwiperSlideChange = useCallback(() => {
    analyticsService.addGACustomEvent('generic_scroll_navigator', {} as GAPromotionConfig);
    checkVisibleSlides(); // Check visibility on every slide change
  }, [analyticsService, checkVisibleSlides]);

  const onCuisineClick = useCallback(
    (action: string, navigatorItem: NavigatorItem, position: number) => {
      triggerAnalyticsEvent('generic_click_navigator', navigatorItem, position);
      onRouterClick(action);
    },
    [triggerAnalyticsEvent, onRouterClick]
  );

  const handleKeyDown = useCallback(
    (e: KeyboardEvent<HTMLElement>, action: string, navigatorItem: NavigatorItem, position: number) => {
      if (e.key === 'Enter' || e.key === ' ') {
        onCuisineClick(action, navigatorItem, position);
      }
    },
    [onCuisineClick]
  );

  const setNavButtonContainerVisibility = useCallback(() => {
    if (!prevEl.current || !nextEl.current) return;
    const bothDisabled =
      nextEl.current.classList.contains('swiper-button-disabled') &&
      prevEl.current.classList.contains('swiper-button-disabled');

    if (bothDisabled) {
      nextEl.current.setAttribute('hidden', 'true');
      prevEl.current.setAttribute('hidden', 'true');
    }
  }, []);

  const checkIfAllSlidesFit = useCallback(() => {
    if (swiperInstance) {
      const { isBeginning, isEnd } = swiperInstance;

      // If both beginning and end are reached simultaneously, all slides fit
      if (isBeginning && isEnd) {
        setShowArrows(false);
      } else {
        setShowArrows(true);
      }
    }
  }, [swiperInstance]);

  useEffect(() => {
    if (swiperInstance) {
      // Check slides on initial mount
      checkVisibleSlides();
      checkIfAllSlidesFit();
    }
  }, [swiperInstance, mainViewTiles, checkIfAllSlidesFit, checkVisibleSlides]);

  return (
    <section className="swiper-section box-swiper home-cuisines-component" aria-label={`${title} section`}>
      {showArrows && (
        <button
          ref={prevEl}
          className="box-swiper-button-inline-prev cuisines-prev mdc-icon-button mat-mdc-icon-button mat-unthemed mat-mdc-button-base"
          aria-label="Previous cuisine"
        >
          <span className="material-icons-round">chevron_left</span>
        </button>
      )}

      {mainViewTiles.length > 0 && (
        <Swiper
          onSwiper={setSwiperInstance}
          onUpdate={setNavButtonContainerVisibility}
          modules={[Navigation, Mousewheel]}
          navigation={{
            prevEl: prevEl.current,
            nextEl: nextEl.current
          }}
          mousewheel={{ forceToAxis: true, releaseOnEdges: false }}
          slidesPerView="auto"
          grabCursor
          spaceBetween={16}
          onSlideChange={onSwiperSlideChange}
          style={{ paddingTop: '2rem', alignItems: 'center' }}
        >
          {mainViewTiles.map((tile: NavigatorItem, index: number) => (
            <SwiperSlide
              key={tile.slug}
              className="swiper-slide"
              onClick={() => onCuisineClick(tile.action, tile, index + 1)}
              onKeyDown={(e) => handleKeyDown(e, tile.action, tile, index + 1)}
              role="link"
              tabIndex={0}
              style={{ flex: 1, minWidth: '132px' }}
            >
              <CollapsibleTileComponent
                tile={collapsibleTiles[index]}
                size="large"
                collapsed={false}
                mediaType={tile.image?.type}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      )}

      {showArrows && (
        <button
          ref={nextEl}
          className="box-swiper-button-inline-next cuisines-next mdc-icon-button mat-mdc-icon-button mat-unthemed mat-mdc-button-base"
          aria-label="Next cuisine"
        >
          <span className="material-icons-round">chevron_right</span>
        </button>
      )}
    </section>
  );
}

const areEqual = (prevProps: NavigatorSectionProps, nextProps: NavigatorSectionProps) => {
  return isEqual(prevProps, nextProps);
};
export default memo(NavigatorSectionComponent, areEqual);
